/**
 * lib/attachments — client-side validation + presigned upload flow.
 *
 * Limits MUST stay in lockstep with src/functions/message-handler/domain/attachments.js
 * and contracts/schemas/common/attachment.schema.json. Update all three together.
 */

export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

export const ALLOWED_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif'
];
export const ALLOWED_PDF_MIME_TYPES = ['application/pdf'];

export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  ...ALLOWED_IMAGE_MIME_TYPES,
  ...ALLOWED_PDF_MIME_TYPES
];

export const MAX_IMAGE_BYTES = 20 * 1024 * 1024; // 20 MiB
export const MAX_PDF_BYTES = 32 * 1024 * 1024;   // 32 MiB

export const ALLOWED_ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_MIME_TYPES.join(',');

export function classifyAttachmentKind(mimeType) {
  if (ALLOWED_IMAGE_MIME_TYPES.includes(mimeType)) return 'image';
  if (ALLOWED_PDF_MIME_TYPES.includes(mimeType)) return 'pdf';
  return null;
}

export function maxBytesForKind(kind) {
  if (kind === 'image') return MAX_IMAGE_BYTES;
  if (kind === 'pdf') return MAX_PDF_BYTES;
  return 0;
}

/**
 * Validate a single File object against allowlist + size caps.
 * Returns null if OK, or a human-readable rejection reason string.
 */
export function rejectionReasonFor(file) {
  if (!file) return 'No file selected.';
  const kind = classifyAttachmentKind(file.type);
  if (!kind) {
    return `${file.name}: unsupported file type. Allowed: images (PNG, JPEG, WEBP, GIF) and PDFs.`;
  }
  const max = maxBytesForKind(kind);
  if (file.size > max) {
    const mb = (max / (1024 * 1024)).toFixed(0);
    return `${file.name}: exceeds ${mb} MB limit for ${kind}.`;
  }
  return null;
}

/**
 * Upload one File to the bucket via a presigned POST policy minted by
 * getUploadUrl. We use POST + multipart/form-data (not PUT) because aws-sdk
 * v2 cannot sign Content-Length into a PUT URL, so a PUT-based flow couldn't
 * cryptographically cap upload size. createPresignedPost lets S3 enforce
 * `content-length-range` itself, rejecting oversized bodies before any
 * bytes are persisted.
 *
 * Note: the `file` form field MUST be appended LAST per the S3 docs.
 *
 * Resolves with the attachment metadata to pass into sendMessage.
 */
export function postToPresignedUrl({ uploadUrl, fields, file, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl, true);
    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded / event.total);
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload.'));
    const formData = new FormData();
    Object.entries(fields || {}).forEach(([key, value]) => {
      formData.append(key, value);
    });
    // S3 policy requires the file field to come after all other fields.
    formData.append('file', file);
    xhr.send(formData);
  });
}

/**
 * Drive the full client-side upload flow for one File:
 *   1. Mint presigned URL via getUploadUrl Apollo mutation.
 *   2. PUT file body directly to S3.
 *   3. Build the AttachmentInput payload for sendMessage.
 *
 * `runUploadUrlMutation` is the bound Apollo mutation result of GET_UPLOAD_URL.
 */
export async function uploadAttachment({
  file,
  conversationId,
  runUploadUrlMutation,
  onProgress
}) {
  const reason = rejectionReasonFor(file);
  if (reason) throw new Error(reason);

  const { data } = await runUploadUrlMutation({
    variables: {
      conversationId,
      fileName: file.name,
      mimeType: file.type,
      sizeBytes: file.size
    }
  });

  const upload = data?.getUploadUrl;
  if (!upload?.uploadUrl || !upload?.key) {
    throw new Error('Server did not return an upload URL.');
  }
  // GraphQL AWSJSON scalar arrives as a serialized JSON string on the client
  // — parse it back to the object S3 needs in the multipart form.
  let fields = upload.fields;
  if (typeof fields === 'string') {
    try {
      fields = JSON.parse(fields);
    } catch (_) {
      throw new Error('Upload form fields could not be parsed.');
    }
  }
  if (!fields || typeof fields !== 'object') {
    throw new Error('Server did not return required upload form fields.');
  }

  await postToPresignedUrl({
    uploadUrl: upload.uploadUrl,
    fields,
    file,
    onProgress
  });

  return {
    key: upload.key,
    mimeType: file.type,
    sizeBytes: file.size,
    fileName: file.name,
    kind: upload.kind || classifyAttachmentKind(file.type) || 'image'
  };
}
